// frontend/src/components/LockedTabContent.jsx
import React from 'react';
import { Button, Typography, Space, Card, Row, Col, Statistic, Badge } from 'antd';
import { CrownOutlined, CheckOutlined, StarOutlined, RocketOutlined, FireOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

// Upgrade URL with UTM tracking
const UPGRADE_URL = 'https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite';

const LockedTabContent = ({ 
  title, 
  description, 
  features, 
  icon, 
  buttonText = "Upgrade to Pro",
  stats = null,
  ctaType = 'standard', // 'standard', 'urgent', 'popular'
  upgradeText = null
}) => {
  
  const getCTAStyle = () => {
    switch (ctaType) {
      case 'urgent':
        return {
          background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
          animation: 'pulse 2s infinite'
        };
      case 'popular':
        return {
          background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
          boxShadow: '0 6px 20px rgba(82, 196, 26, 0.4)'
        };
      default:
        return {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
        };
    }
  };

  const getCTAIcon = () => {
    switch (ctaType) {
      case 'urgent':
        return <FireOutlined />;
      case 'popular':
        return <ThunderboltOutlined />;
      default:
        return <RocketOutlined />;
    }
  };

  return (
    <div style={{ 
      minHeight: '500px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      borderRadius: '12px',
      padding: '40px 20px'
    }}>
      <Card 
        style={{ 
          maxWidth: '700px', 
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          border: 'none'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        {/* Badge for popular features */}
        {ctaType === 'popular' && (
          <div style={{ position: 'absolute', top: -10, right: 20 }}>
            <Badge.Ribbon text="Most Popular" color="green" />
          </div>
        )}

        {/* Icon */}
        <div style={{ marginBottom: '24px' }}>
          {icon || <CrownOutlined style={{ fontSize: '64px', color: '#1890ff' }} />}
        </div>
        
        {/* Title */}
        <Title level={2} style={{ color: '#1890ff', marginBottom: '16px' }}>
          {title}
        </Title>
        
        {/* Description */}
        <Paragraph style={{ 
          fontSize: '16px', 
          color: '#666', 
          marginBottom: '32px',
          lineHeight: '1.6'
        }}>
          {description}
        </Paragraph>

        {/* Stats Section */}
        {stats && (
          <Row gutter={[24, 16]} style={{ marginBottom: '32px' }}>
            {stats.map((stat, index) => (
              <Col xs={24} sm={8} key={index}>
                <Card size="small" style={{ background: '#f8f9fa' }}>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    suffix={stat.suffix}
                    prefix={stat.prefix}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
        
        {/* Features List */}
        {features && features.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <Title level={4} style={{ color: '#333', marginBottom: '20px' }}>
              ✨ What's included:
            </Title>
            
            <Row gutter={[16, 16]}>
              {features.map((feature, index) => (
                <Col xs={24} sm={12} key={index}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-start',
                    padding: '10px 14px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e6f4ff';
                    e.target.style.borderColor = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f8f9fa';
                    e.target.style.borderColor = '#e9ecef';
                  }}
                  >
                    <CheckOutlined style={{ 
                      color: '#52c41a', 
                      marginRight: '12px',
                      fontSize: '16px'
                    }} />
                    <Text style={{ fontSize: '14px', fontWeight: '500' }}>{feature}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
        
        {/* Enhanced Call to Action */}
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ position: 'relative' }}>
            <Button 
              type="primary" 
              size="large"
              icon={getCTAIcon()}
              onClick={() => window.open(UPGRADE_URL, '_blank')}
              style={{
                height: '52px',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '10px',
                width: '100%',
                maxWidth: '300px',
                ...getCTAStyle()
              }}
            >
              {buttonText}
            </Button>
            
            {ctaType === 'urgent' && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#ff4d4f',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: 'bold'
              }}>
                LIMITED TIME
              </div>
            )}
          </div>

          {/* Upgrade benefits */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f4ff 100%)',
            padding: '20px',
            borderRadius: '10px',
            border: '1px solid #d6e4ff'
          }}>
            <Text strong style={{ color: '#1890ff', fontSize: '15px' }}>
              🚀 Instant Access After Upgrade
            </Text>
            <div style={{ marginTop: '12px' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  All your workflows and data preserved
                </Text>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  Immediate feature activation
                </Text>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  30-day money-back guarantee
                </Text>
              </Space>
            </div>
          </div>

          {/* Additional upgrade text */}
          {upgradeText && (
            <div style={{
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '16px'
            }}>
              <Text style={{ color: '#d48806', fontSize: '14px' }}>
                💡 {upgradeText}
              </Text>
            </div>
          )}
        </Space>
        
        {/* Support CTA */}
        <div style={{ 
          marginTop: '32px', 
          padding: '16px', 
          background: '#f9f9f9', 
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <Text style={{ fontSize: '14px', color: '#666' }}>
            Questions about upgrading? 
            <Button 
              type="link" 
              size="small"
              onClick={() => window.open('https://wpaiworkflowautomation.com/contact', '_blank')}
              style={{ padding: '0 4px', color: '#1890ff' }}
            >
              Contact our team
            </Button>
            for personalized assistance.
          </Text>
        </div>
      </Card>
    </div>
  );
};

// Enhanced predefined configurations for common locked tabs
export const GoogleIntegrationLocked = () => (
  <LockedTabContent 
    title="Google Integration"
    description="Connect your workflows to Google services using your own secure Google application. Save workflow outputs directly to Google Sheets and create documents in Google Drive with complete privacy and control."
    features={[
      "Save workflow outputs to Google Sheets",
      "Generate documents in Google Drive", 
      "Use your own Google Cloud application",
      "Complete data privacy & security",
      "Direct integration with workflow outputs",
      "Automated file organization in Drive"
    ]}
    stats={[
      { title: "Privacy", value: "100%", suffix: " Secure", prefix: "🔒" },
      { title: "Setup Time", value: "5", suffix: "min", prefix: "⚡" },
      { title: "Google Services", value: "2", prefix: "🔗" }
    ]}
    ctaType="popular"
    upgradeText="Save time by automatically organizing your AI outputs in Google Workspace"
  />
);

export const CostManagementLocked = () => (
  <LockedTabContent 
    title="Cost Management & Analytics" 
    description="Monitor your AI usage costs and get detailed insights into your workflow spending. Track usage across different AI models and optimize your costs with comprehensive analytics."
    features={[
      "Real-time AI cost tracking",
      "Detailed usage analytics & reports", 
      "Per-workflow cost breakdown",
      "AI model usage comparison",
      "Monthly spending summaries",
      "Cost optimization insights"
    ]}
    stats={[
      { title: "Cost Tracking", value: "Real-time", prefix: "📊" },
      { title: "Reports", value: "Detailed", prefix: "📈" },
      { title: "Models", value: "All", prefix: "🤖" }
    ]}
    ctaType="standard"
    upgradeText="Pro users gain visibility into their AI spending and can optimize costs effectively"
  />
);

export const KnowledgeBaseLocked = () => (
  <LockedTabContent 
    title="Knowledge Base & Vector Stores"
    description="Create intelligent knowledge bases using OpenAI vector stores and your WordPress content. Add pages, posts, and products directly to chatbot knowledge for contextual AI responses."
    features={[
      "OpenAI vector store integration",
      "Direct WordPress content integration", 
      "Add pages, posts & products to chatbot",
      "Coming soon: Supabase support",
      "Coming soon: Pinecone integration",
      "Intelligent content retrieval for AI"
    ]}
    stats={[
      { title: "Content Types", value: "All", prefix: "📄" },
      { title: "Vector Stores", value: "3+", prefix: "🗄️" },
      { title: "Integration", value: "Direct", prefix: "⚡" }
    ]}
    ctaType="standard"
    upgradeText="Turn your WordPress content into intelligent knowledge for your AI chatbots"
  />
);

export const WhitelabelingLocked = () => (
  <LockedTabContent 
    title="White-labeling & Customization"
    description="Customize the plugin branding with your own logo, colors, and styling. Perfect for agencies and businesses who want to provide AI workflow automation under their own brand."
    features={[
      "Custom logo & branding upload",
      "Brand color customization", 
      "Email template personalization",
      "Agency white-labeling options",
      "Custom CSS styling capabilities",
      "Client-ready interface design"
    ]}
    stats={[
      { title: "Brand Elements", value: "Full", prefix: "🎨" },
      { title: "Setup Time", value: "10", suffix: "min", prefix: "⚡" },
      { title: "Client Ready", value: "100%", prefix: "✅" }
    ]}
    buttonText="Upgrade to Agency License"
    ctaType="urgent"
    upgradeText="Perfect for agencies managing multiple client accounts"
  />
);

// Add CSS for pulse animation
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;
document.head.appendChild(style);

export default LockedTabContent;